import * as React from "react";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { IMobileOffer } from "@nju/data-access-portal-offers";
import {
  IMobileOrchestrator,
  IMultiOrderOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { useUser } from "@nju/data-access-portal-user";
import { Icon, IconName } from "@nju/icons";
import { BlockText, Heading, IconListElement, Separator, Text } from "@nju/ui";
import { formatPhoneNumber, IAddress, POSITIVE_DIGITS } from "@nju/utils";
import { Title } from "./partials";
import { Info } from "./partials/Info";
import { OrderStatus } from "./partials/OrderStatus";
import { Payments } from "./partials/Payments";

interface IMobileStatusBannerDetails {
  offer: IMobileOffer;
  status: string;
  contactNumber: string | undefined;
  deliveryAddress: IAddress | undefined;
  deliveryMethodId: IMultiOrderOrchestrator["deliveryMethodId"];
  note: string | undefined;
  clientNumber: string | undefined;
  transferDate: string | undefined;
  orchestratorState: IMobileOrchestrator["state"];
  iconName: IconName;
  statusText: string | undefined;
  statusIcon: IconName | undefined;
}

export function MobileStatusBannerDetails({
  offer,
  status,
  contactNumber,
  deliveryAddress,
  deliveryMethodId,
  note,
  clientNumber,
  transferDate,
  orchestratorState,
  iconName,
  statusText,
  statusIcon,
}: IMobileStatusBannerDetails) {
  const {
    totalRecurringPrice,
    shortFeatures,
    extraShortFeatures,
    discountFeatures,
    bonusFeatures,
  } = offer;
  const { primaryPaymentMethod } = useUser();
  const icon = "mr-4 w-5 h-5";
  const detailsStatusText = statusText ? statusText : status;
  const detailsIconName = statusIcon ? statusIcon : iconName;
  const memberOrchestrator = useMemberOrchestrator();
  const selectedOrchestrator = memberOrchestrator?.mobile;
  const mobilePrice =
    selectedOrchestrator?.orderSummary?.totalRecurringOrderPrice ??
    totalRecurringPrice;
  const activationFeeIds = [
    "P-NJU-Activation-Payment",
    "P-NJU-Activation-Payment-Postpaid",
  ];
  const activationFee =
    selectedOrchestrator?.orderSummary?.items?.find((item) => {
      return activationFeeIds.includes(item.offerId);
    })?.offerPrice ?? 0;

  return (
    <>
      <Separator size="s" tone="invisible" />
      <Title offerCategory={selectedOrchestrator?.offerCategory} />
      <Separator size="s" tone="invisible" />
      {clientNumber && (
        <>
          <div className="flex items-start">
            <Icon className={icon} name="sim-card" tone="accent" />
            <div>
              <Text
                size={{ "@onlyMobile": "m", "@fromTablet": "l" }}
                weight="bold"
              >
                Twój numer
              </Text>
              <BlockText
                className="pt-1"
                size={{ "@onlyMobile": "s", "@fromTablet": "m" }}
              >
                {formatPhoneNumber(
                  clientNumber,
                  POSITIVE_DIGITS,
                  " ",
                  "+48",
                  true,
                )}
              </BlockText>
            </div>
          </div>
          <Separator size="s" tone="invisible" />
        </>
      )}
      <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
        rabaty
      </Heading>
      <Separator size="s" thickness="medium" tone="accent" />
      <Separator size="xxs" tone="invisible" />
      {discountFeatures?.map((feature, indexFeature) => {
        //const check if feature name has partly suffix "_HIGHLIGHT"
        const isDashedBorder = feature.name.endsWith("_HIGHLIGHT");
        return (
          <React.Fragment key={`discount_${feature.name}_${indexFeature}`}>
            {isDashedBorder && (
              <IconListElement
                borderStyle="dashed"
                darkTooltipIcon={true}
                iconName={feature.icon as IconName}
                iconSize="medium"
                mode="markdown"
                text={feature.description.pl}
                tooltipContent={feature.additionalDescription?.pl}
              />
            )}
          </React.Fragment>
        );
      })}
      {bonusFeatures && (
        <>
          <Separator size="xs" tone="invisible" />
          <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
            bonusy
          </Heading>
          <Separator size="xs" thickness="medium" tone="accent" />
          <Separator size="xxs" tone="invisible" />
          {bonusFeatures?.map((feature) => {
            //const check if feature name has partly suffix "_HIGHLIGHT"
            const isDashedBorder = feature.name.endsWith("_HIGHLIGHT");
            return (
              <React.Fragment key={feature.name}>
                {isDashedBorder && (
                  <IconListElement
                    borderStyle="dashed"
                    darkTooltipIcon={true}
                    iconName={feature.icon as IconName}
                    iconSize="medium"
                    mode="markdown"
                    text={feature.description.pl}
                    tooltipContent={feature.additionalDescription?.pl}
                  />
                )}
              </React.Fragment>
            );
          })}
        </>
      )}
      <Separator size="xs" tone="invisible" />
      <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
        oferta
      </Heading>
      <Separator size="s" thickness="medium" tone="accent" />
      <div className="break-inside-avoid columns-1">
        {shortFeatures.map((feature, indexFeature) => {
          return (
            <IconListElement
              iconAlign="start"
              iconName={feature.icon as IconName}
              iconSize="big"
              key={`short_${feature.name}_${indexFeature}`}
              mode="markdown"
              text={feature.description.pl}
            />
          );
        })}
      </div>
      {extraShortFeatures && (
        <>
          <Separator size="s" tone="invisible" />
          <Heading font="ttfirs" tone="neutral" variant="h4" weight="bold">
            dodatkowo
          </Heading>
          <Separator size="s" />
          {extraShortFeatures?.map((extras, indexExtras) => {
            return (
              <IconListElement
                iconAlign="center"
                iconName={extras.icon as IconName}
                iconSize="medium"
                key={`extra_${extras.name}_${indexExtras}`}
                mode="markdown"
                text={extras.description.pl}
              />
            );
          })}
        </>
      )}
      <Separator size="m" />
      <OrderStatus
        iconName={detailsIconName}
        orchestratorState={orchestratorState}
        status={detailsStatusText}
        transferDate={transferDate}
      />
      <Separator size="m" />
      <Payments
        activationFee={activationFee}
        paymentMethod={primaryPaymentMethod}
        subscriptionPrice={mobilePrice}
      />
      {deliveryAddress && deliveryMethodId !== "ESIM" && <Separator size="m" />}
      <Info
        contactNumber={contactNumber}
        deliveryAddress={deliveryAddress}
        deliveryMethodId={deliveryMethodId}
        note={note}
      />
    </>
  );
}
